/**
 * Relation type constants for graph relationships
 * @module constants/relation-types
 */

import type { ObjectType } from './object-types.js';

/** ArchiMate-compatible relation types */
export const RELATION_TYPES = [
  'REALIZES',
  'USES',
  'DEPENDS_ON',
  'OWNS',
  'BELONGS_TO',
  'INTEGRATES_WITH',
] as const;
export type RelationType = (typeof RELATION_TYPES)[number];

/** Relation strength levels */
export const RELATION_STRENGTHS = ['full', 'partial'] as const;
export type RelationStrength = (typeof RELATION_STRENGTHS)[number];

/** Usage types for USES relation */
export const USAGE_TYPES = ['runtime', 'build', 'test'] as const;
export type UsageType = (typeof USAGE_TYPES)[number];

/** Dependency types for DEPENDS_ON relation */
export const DEPENDENCY_TYPES = ['critical', 'standard', 'optional'] as const;
export type DependencyType = (typeof DEPENDENCY_TYPES)[number];

/** Ownership types for OWNS relation */
export const OWNERSHIP_TYPES = ['technical', 'business', 'operational'] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];

/** Integration methods for INTEGRATES_WITH relation */
export const INTEGRATION_METHODS = ['api', 'file', 'event', 'database'] as const;
export type IntegrationMethod = (typeof INTEGRATION_METHODS)[number];

/** Data flow directions */
export const DATA_FLOWS = ['unidirectional', 'bidirectional'] as const;
export type DataFlow = (typeof DATA_FLOWS)[number];

/** Direction for relation queries */
export const RELATION_DIRECTIONS = ['incoming', 'outgoing', 'both'] as const;
export type RelationDirection = (typeof RELATION_DIRECTIONS)[number];

/**
 * Valid source-target combinations for each relation type
 * Used for validation when creating relations
 */
export const VALID_RELATION_COMBINATIONS: Record<
  RelationType,
  Array<{ source: ObjectType[]; target: ObjectType[] }>
> = {
  REALIZES: [
    { source: ['Application', 'Technology'], target: ['BusinessCapability'] },
  ],
  USES: [
    { source: ['Application'], target: ['Technology'] },
    { source: ['Technology'], target: ['Technology'] },
  ],
  DEPENDS_ON: [
    { source: ['Application'], target: ['Application'] },
    { source: ['Technology'], target: ['Technology'] },
  ],
  OWNS: [
    { source: ['Person', 'Organization'], target: ['Application', 'Technology'] },
  ],
  BELONGS_TO: [
    { source: ['Person'], target: ['Organization'] },
    { source: ['Organization'], target: ['Organization'] },
  ],
  INTEGRATES_WITH: [
    { source: ['Application'], target: ['Application'] },
  ],
} as const;

/**
 * Check if a relation type is valid between two object types
 */
export function isValidRelation(
  relationType: RelationType,
  sourceType: ObjectType,
  targetType: ObjectType
): boolean {
  const combinations = VALID_RELATION_COMBINATIONS[relationType];
  return combinations.some(
    (combo) => combo.source.includes(sourceType) && combo.target.includes(targetType)
  );
}
